import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, 
  TrendingUp, 
  DollarSign, 
  Clock, 
  Bot,
  ArrowUpRight,
  Plus,
  ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { KYCBanner } from '@/components/KYCBanner';
import { formatCurrency, formatDate, getStatusColor } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ConsumerDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, requestsRes] = await Promise.all([
          axios.get(`${API}/dashboard/consumer`),
          axios.get(`${API}/loans/requests`)
        ]);
        setStats(statsRes.data);
        setRequests(requestsRes.data.slice(0, 5));
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const statCards = [
    {
      icon: FileText,
      label: 'Active Requests',
      value: stats?.active_requests || 0,
      color: 'text-blue-500'
    },
    {
      icon: DollarSign,
      label: 'Total Borrowed',
      value: formatCurrency(stats?.total_borrowed || 0),
      color: 'text-green-500'
    },
    {
      icon: Clock,
      label: 'Pending Bids',
      value: stats?.pending_bids || 0,
      color: 'text-yellow-500'
    },
    {
      icon: Bot,
      label: 'Credit Score',
      value: stats?.credit_score || 0,
      color: 'text-purple-500'
    }
  ];

  if (loading) {
    return (
      <DashboardLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Dashboard">
      <KYCBanner />
      {/* Welcome */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-2">Welcome back, {user?.name}!</h2>
        <p className="text-muted-foreground" data-testid="dashboard-usp">
          We help Borrowers monetize their credit.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
          >
            <Card className="glass border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <stat.icon className={`w-8 h-8 ${stat.color}`} />
                  <ArrowUpRight className="w-5 h-5 text-muted-foreground" />
                </div>
                <div className="text-3xl font-bold mb-1">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <Card className="glass border-white/10">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <Link to="/consumer/new-request">
                <Button 
                  data-testid="quick-new-request-btn"
                  className="w-full justify-between gradient-primary"
                >
                  <span className="flex items-center gap-2">
                    <Plus className="w-5 h-5" />
                    Create New Loan Request
                  </span>
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/consumer/ai-analysis">
                <Button 
                  data-testid="quick-ai-analysis-btn"
                  variant="outline" 
                  className="w-full justify-between border-white/10"
                >
                  <span className="flex items-center gap-2">
                    <Bot className="w-5 h-5" />
                    Check AI Credit Analysis
                  </span>
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-white/10">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4">Credit Score</h3>
            <div className="flex items-center gap-6">
              <div className="relative w-32 h-32">
                <svg className="w-full h-full -rotate-90">
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="12"
                    className="text-white/10"
                  />
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    fill="none"
                    stroke="url(#scoreGradient)"
                    strokeWidth="12"
                    strokeLinecap="round"
                    strokeDasharray={`${((stats?.credit_score || 0) / 850) * 352} 352`}
                  />
                  <defs>
                    <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#3B82F6" />
                      <stop offset="100%" stopColor="#8B5CF6" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-3xl font-bold">{stats?.credit_score || 0}</span>
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground mb-1">Score Range</div>
                <div className="text-lg font-semibold mb-3">300 - 850</div>
                <Badge className={
                  (stats?.credit_score || 0) >= 750 ? 'bg-green-500/10 text-green-500' :
                  (stats?.credit_score || 0) >= 650 ? 'bg-yellow-500/10 text-yellow-500' :
                  'bg-red-500/10 text-red-500'
                }>
                  {(stats?.credit_score || 0) >= 750 ? 'Excellent' :
                   (stats?.credit_score || 0) >= 650 ? 'Good' :
                   (stats?.credit_score || 0) >= 550 ? 'Fair' : 'Poor'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Requests */}
      <Card className="glass border-white/10">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Recent Loan Requests</CardTitle>
          <Link to="/consumer/requests">
            <Button variant="ghost" size="sm">
              View All
              <ChevronRight className="ml-1 w-4 h-4" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No loan requests yet</p>
              <Link to="/consumer/new-request">
                <Button className="mt-4 gradient-primary">
                  Create Your First Request
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((req) => (
                <Link 
                  key={req.id} 
                  to={`/consumer/requests/${req.id}`}
                  className="block"
                >
                  <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                    <div>
                      <div className="font-medium mb-1">{formatCurrency(req.amount)}</div>
                      <div className="text-sm text-muted-foreground">
                        {req.purpose} • {req.term_months} months
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={getStatusColor(req.status)}>
                        {req.status}
                      </Badge>
                      <div className="text-sm text-muted-foreground mt-1">
                        {req.bids_count} bids
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
